import { Button } from "@/components/ui/button";
import { UserCheck, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useCurrentUser } from "@/hooks/platform/useCurrentUser";
import { useFollowing, useToggleFollow } from "@/hooks/platform/useSocial";

interface Props {
  targetId?: string | null;
  /** Sample/showcase people have no real account to follow. */
  sample?: boolean;
  size?: "sm" | "default";
  className?: string;
}

/** Reusable follow / unfollow control used in every bio pop-up. */
const FollowButton = ({ targetId, sample, size = "sm", className }: Props) => {
  const { userId } = useCurrentUser();
  const { data: following = [] } = useFollowing(userId);
  const toggle = useToggleFollow();

  if (!targetId) return null;
  const isFollowing = following.some((f) => f.following_id === targetId);
  const isSelf = userId === targetId;
  if (isSelf) return null;

  const onClick = () => {
    if (sample) { toast.info("Sample profile — follow real creators and venues from the directory."); return; }
    if (!userId) { toast.error("Sign in to follow"); return; }
    toggle.mutate({ followerId: userId, followingId: targetId, isFollowing });
  };

  return (
    <Button
      size={size}
      variant={isFollowing ? "outline" : "default"}
      className={className}
      disabled={toggle.isPending}
      onClick={onClick}
    >
      {isFollowing ? <><UserCheck className="w-3.5 h-3.5 mr-1" />Following</> : <><UserPlus className="w-3.5 h-3.5 mr-1" />Follow</>}
    </Button>
  );
};

export default FollowButton;
